import { useState, useEffect } from 'react';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';
import toast from 'react-hot-toast';
import { Pencil, Trash2, Copy } from 'lucide-react';
import BusinessSettingsModal from '../../components/invoices/BusinessSettingsModal';
import InvoiceWizard from '../../components/invoices/InvoiceWizard';
import InvoiceView from '../../components/invoices/InvoiceView';

const STATUS_OPTIONS = ['PO', 'issued', 'paid', 'cancelled'];

const getApiBaseUrl = () => {
    let url = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    return url.endsWith('/') ? url.slice(0, -1) : url;
};

const API_BASE_URL = getApiBaseUrl();

interface BusinessSetting {
    id: string;
    businessName: string;
    invoicePrefix: string;
    gstin: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    addressLine3: string | null;
    email: string | null;
    phoneNumber: string | null;
    invoiceCounter: number;
    isActive: boolean;
    _count?: {
        invoices: number;
    };
}

interface Invoice {
    id: string;
    invoiceNumber: string;
    businessSettingId: string;
    invoiceDate: string;
    dueDate?: string;
    clientType: string;
    clientGymId?: string;
    clientName: string;
    clientAddress?: string;
    clientPhone?: string;
    clientGstin?: string;
    totalAmount: number;
    status: string;
    invoiceType: string;
    businessSetting: BusinessSetting;
    lineItems: Array<{
        description: string;
        quantity: number;
        unitPrice: number;
    }>;
    discountPercent: number;
    discountAmount: number;
    taxPercent: number;
    notes?: string;
    termsAndConditions?: string;
}

export default function ManageInvoicesPage() {
    const { token } = useSuperAdminAuth();
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loading, setLoading] = useState(true);
    const [showBusinessSettings, setShowBusinessSettings] = useState(false);
    const [showInvoiceWizard, setShowInvoiceWizard] = useState(false);
    const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
    const [duplicatePrefill, setDuplicatePrefill] = useState<any>(null);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [modalLoading, setModalLoading] = useState(false);

    useEffect(() => {
        if (token) {
            fetchInvoices();
        }
    }, [token, statusFilter]);

    const fetchInvoices = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (statusFilter !== 'all') {
                params.append('status', statusFilter);
            }

            const response = await fetch(`${API_BASE_URL}/api/saas/invoices/invoices?${params.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error('Failed to fetch invoices');

            const data = await response.json();
            setInvoices(data.data || []);
        } catch (error) {
            console.error('Error fetching invoices:', error);
            toast.error('Failed to fetch invoices');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteInvoice = async (id: string) => {
        if (!confirm('Are you sure you want to delete this invoice?')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/api/saas/invoices/invoices/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error('Failed to delete invoice');

            toast.success('Invoice deleted successfully');
            fetchInvoices();
        } catch (error) {
            console.error('Error deleting invoice:', error);
            toast.error('Failed to delete invoice');
        }
    };

    const handleStatusChange = async (invoiceId: string, status: string) => {
        const previous = invoices;
        setInvoices((prev) => prev.map((inv) => (inv.id === invoiceId ? { ...inv, status } : inv)));

        try {
            const response = await fetch(`${API_BASE_URL}/api/saas/invoices/invoices/${invoiceId}/status`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ status }),
            });

            if (!response.ok) throw new Error('Failed to update status');

            toast.success('Invoice status updated');
        } catch (error) {
            console.error('Error updating invoice status:', error);
            toast.error('Failed to update invoice status');
            setInvoices(previous);
        }
    };

    const handleInvoiceCreated = () => {
        setShowInvoiceWizard(false);
        setEditingInvoice(null);
        setDuplicatePrefill(null);
        fetchInvoices();
    };

    const handleDuplicateInvoice = async (invoice: Invoice) => {
        setModalLoading(true);
        try {
            // Fetch full invoice details (line items etc. aren't in the list payload)
            const response = await fetch(`${API_BASE_URL}/api/saas/invoices/invoices/${invoice.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error('Failed to fetch invoice details');

            const data = await response.json();
            const source = data.data;

            setDuplicatePrefill({
                businessSettingId: source.businessSettingId,
                clientType: source.clientType,
                clientGymId: source.clientGymId,
                clientName: source.clientName,
                clientAddress: source.clientAddress,
                clientPhone: source.clientPhone,
                clientGstin: source.clientGstin,
                lineItems: source.lineItems,
                discountPercent: source.discountPercent,
                discountAmount: source.discountAmount,
                taxPercent: source.taxPercent,
                notes: source.notes,
                termsAndConditions: source.termsAndConditions,
            });
            setEditingInvoice(null);
            setShowInvoiceWizard(true);
        } catch (error) {
            console.error('Error duplicating invoice:', error);
            toast.error('Failed to load invoice details');
        } finally {
            setModalLoading(false);
        }
    };

    const handleEditInvoice = async (invoice: Invoice) => {
        setModalLoading(true);
        try {
            // Fetch full invoice details
            const response = await fetch(`${API_BASE_URL}/api/saas/invoices/invoices/${invoice.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error('Failed to fetch invoice details');

            const data = await response.json();
            setEditingInvoice(data.data);
            setShowInvoiceWizard(true);
        } catch (error) {
            console.error('Error fetching invoice details:', error);
            toast.error('Failed to load invoice details');
        } finally {
            setModalLoading(false);
        }
    };

    const getStatusBadgeClass = (status: string) => {
        switch (status) {
            case 'paid':
                return 'bg-green-100 text-green-800';
            case 'issued':
                return 'bg-blue-100 text-blue-800';
            case 'PO':
                return 'bg-yellow-100 text-yellow-800';
            case 'cancelled':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-6">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Manage Invoices</h1>
                            <p className="mt-1 text-sm text-gray-600">
                                Create and manage invoices for your business
                            </p>
                        </div>
                        <button
                            onClick={() => setShowBusinessSettings(true)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            ⚙️ Invoice Settings
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Actions Bar */}
                <div className="mb-6 flex justify-between items-center">
                    <div className="flex gap-4 items-center">
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="all">All Statuses</option>
                            <option value="PO">Purchase Order</option>
                            <option value="issued">Issued</option>
                            <option value="paid">Paid</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                        <span className="text-sm text-gray-600">
                            {invoices.length} invoice{invoices.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                    <button
                        onClick={() => {
                            setEditingInvoice(null);
                            setDuplicatePrefill(null);
                            setShowInvoiceWizard(true);
                        }}
                        className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium"
                    >
                        + Create New Invoice
                    </button>
                </div>

                {/* Invoices List */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        <p className="mt-2 text-gray-600">Loading invoices...</p>
                    </div>
                ) : invoices.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-lg shadow">
                        <p className="text-gray-600">No invoices found</p>
                        <button
                            onClick={() => setShowInvoiceWizard(true)}
                            className="mt-4 px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                        >
                            Create Your First Invoice
                        </button>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <div className="overflow-y-auto max-h-[65vh]">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Invoice #
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Client
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Business
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Amount
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {invoices.map((invoice) => (
                                        <tr
                                            key={invoice.id}
                                            className="hover:bg-gray-50 cursor-pointer"
                                            onClick={() => setSelectedInvoiceId(invoice.id)}
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {invoice.invoiceNumber}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {invoice.invoiceType === 'po' ? 'Purchase Order' : 'Invoice'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {new Date(invoice.invoiceDate).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {invoice.clientName}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {invoice.businessSetting.businessName}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                ₹{parseFloat(invoice.totalAmount.toString()).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                <select
                                                    value={invoice.status}
                                                    onChange={(e) => handleStatusChange(invoice.id, e.target.value)}
                                                    className={`text-xs font-medium rounded-full border-0 py-1 pl-2 pr-6 cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500 ${getStatusBadgeClass(invoice.status)}`}
                                                >
                                                    {STATUS_OPTIONS.map((s) => (
                                                        <option key={s} value={s}>
                                                            {s.toUpperCase()}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                                                <div className="flex gap-3 justify-end" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={() => handleEditInvoice(invoice)}
                                                        className="text-blue-600 hover:text-blue-900"
                                                        title="Edit"
                                                    >
                                                        <Pencil className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDuplicateInvoice(invoice)}
                                                        className="text-gray-600 hover:text-gray-900"
                                                        title="Duplicate"
                                                    >
                                                        <Copy className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteInvoice(invoice.id)}
                                                        className="text-red-600 hover:text-red-900"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            {showBusinessSettings && (
                <BusinessSettingsModal
                    token={token || ''}
                    onClose={() => setShowBusinessSettings(false)}
                />
            )}

            {selectedInvoiceId && (
                <InvoiceView
                    token={token || ''}
                    invoiceId={selectedInvoiceId}
                    onClose={() => setSelectedInvoiceId(null)}
                />
            )}

            {modalLoading && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-[70]">
                    <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                        <p className="mt-4 text-gray-700 font-medium">Loading details...</p>
                    </div>
                </div>
            )}

            {showInvoiceWizard && (
                <InvoiceWizard
                    token={token || ''}
                    invoice={editingInvoice}
                    prefilledData={duplicatePrefill}
                    onClose={() => {
                        setShowInvoiceWizard(false);
                        setEditingInvoice(null);
                        setDuplicatePrefill(null);
                    }}
                    onSuccess={handleInvoiceCreated}
                />
            )}
        </div>
    );
}
